/**
 * A modal sheet that rises from the bottom of the screen.
 *
 * Phones get this instead of the desktop sidebar and side panel. It is anchored
 * to the bottom because that is where the thumb is: a dialog centred in the
 * viewport puts its close button and its controls at the top of a 6" screen,
 * which is the hardest place on the device to reach.
 */

import { useEffect, useRef, type ReactNode } from 'react'

/**
 * Written out in full rather than built as `${bp}:hidden`, because Tailwind
 * scans the source for literal class names and would not emit a composed one.
 */
const HIDE_FROM = {
  lg: 'lg:hidden',
  xl: 'xl:hidden',
} as const

interface BottomSheetProps {
  open: boolean
  onClose: () => void
  title: string
  children: ReactNode
  /** Fraction of the viewport the sheet may grow to. Defaults to 85%. */
  maxHeight?: string
  /**
   * Breakpoint at which the desktop equivalent takes over and the sheet must
   * stop rendering. Has to match whatever breakpoint reveals that desktop
   * element, or there is a window of widths with neither.
   */
  hideFrom?: keyof typeof HIDE_FROM
}

export default function BottomSheet({
  open,
  onClose,
  title,
  children,
  maxHeight = '85dvh',
  hideFrom = 'lg',
}: BottomSheetProps) {
  const panelRef = useRef<HTMLDivElement>(null)

  // Escape closes it, matching the desktop panel and every native sheet.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  // Move focus into the sheet so the keyboard and screen reader follow it.
  useEffect(() => {
    if (open) panelRef.current?.focus()
  }, [open])

  if (!open) return null

  return (
    <div className={`fixed inset-0 z-50 flex flex-col justify-end ${HIDE_FROM[hideFrom]}`}>
      {/* Scrim. A button rather than a div so the dismiss is reachable by
          keyboard and announced, instead of being a click handler on nothing. */}
      <button
        type="button"
        aria-label={`Close ${title}`}
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-ink-950/70 backdrop-blur-sm"
      />

      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={{ maxHeight }}
        className="pb-safe relative flex animate-fade-up flex-col overflow-hidden rounded-t-2xl
                   border-t border-ink-700 bg-ink-900 shadow-panel outline-none"
      >
        <div className="flex shrink-0 items-center gap-3 border-b border-ink-800 px-4 py-2">
          {/* The grabber reads as "this panel came from the bottom edge and goes
              back there", which a bare header does not. */}
          <div className="absolute left-1/2 top-1.5 h-1 w-9 -translate-x-1/2 rounded-full bg-ink-600" />
          <h2 className="mt-1.5 flex-1 text-sm font-bold text-slate-100">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={`Close ${title}`}
            className="mt-1.5 grid h-11 w-11 place-items-center rounded-xl text-ink-400 active:bg-ink-800"
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <path d="m6 6 12 12M18 6 6 18" />
            </svg>
          </button>
        </div>

        {/* overscroll-contain stops a flick at the end of this list from scrolling
            the page behind the sheet. */}
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">{children}</div>
      </div>
    </div>
  )
}
